import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useHubChain from '../../hooks/useHubChain';
import { markArriving, stopLabel } from '../../utils/hubs/chain';

/*
 * The visible way along the swipe chain, Home -> Bio-Bank -> Experiments
 * (decisions D10, D12, D24): the page swipe (components/Layout.js) is only the
 * fast path; this works with a mouse, a keyboard and a screen reader (WCAG
 * 2.5.1). Shown on Home and on the hubs in the chain, at every width.
 *
 * Laid out PHYSICALLY (dir="ltr"), like the swipe: a left-to-right swipe
 * brings the next stop in from the left, so the next stop is on the left, the
 * previous one on the right, and the dots run Experiments, Bank, Home. In
 * Hebrew that is reading order; in English it reads the other way, on
 * purpose (D24).
 */
const shortLabel = (stop) => (stop.key === 'home' ? 'nav:home' : `hubs:${stop.key}.short`);

export default function HubPager() {
  const { t } = useTranslation(['hubs', 'nav']);
  const { chain, index, prev, next, go } = useHubChain();
  if (index < 0 || chain.length < 2) return null;
  const here = chain[index];
  const physical = [...chain].reverse();

  return (
    <nav className="hub-pager" dir="ltr" aria-label={t('hubs:pager.label')} data-testid="hub-pager">
      {next ? (
        <button type="button" className="hub-pager-step" onClick={() => go('next')}
          aria-label={t('hubs:pager.next', { hub: t(stopLabel(next)) })} data-testid="hub-pager-next">
          <span aria-hidden="true" className="hub-pager-chevron">‹</span>
          <span>{t(shortLabel(next))}</span>
        </button>
      ) : <span className="hub-pager-step hub-pager-step--none" aria-hidden="true" />}
      <ol className="hub-pager-dots">
        {physical.map((stop) => (
          <li key={stop.key}>
            <Link to={stop.path} className="hub-pager-dot" aria-current={stop === here ? 'page' : undefined}
              aria-label={t(stopLabel(stop))} title={t(stopLabel(stop))} data-testid={`hub-pager-dot-${stop.key}`}
              onClick={() => { if (stop !== here) markArriving(stop); }} />
          </li>
        ))}
      </ol>
      {prev ? (
        <button type="button" className="hub-pager-step" onClick={() => go('prev')}
          aria-label={t('hubs:pager.prev', { hub: t(stopLabel(prev)) })} data-testid="hub-pager-prev">
          <span>{t(shortLabel(prev))}</span>
          <span aria-hidden="true" className="hub-pager-chevron">›</span>
        </button>
      ) : <span className="hub-pager-step hub-pager-step--none" aria-hidden="true" />}
    </nav>
  );
}
